// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const DOC_TYPES = [
  { id: 'privacy', name: 'Privacy Policy', icon: '\uD83D\uDD12', desc: 'PIPEDA/GDPR-compliant privacy policy', status: 'generated' },
  { id: 'terms', name: 'Terms of Service', icon: '\uD83D\uDCDC', desc: 'Terms and conditions for your business', status: 'generated' },
  { id: 'cookie', name: 'Cookie Policy', icon: '\uD83C\uDF6A', desc: 'Cookie usage disclosure and consent', status: 'generated' },
  { id: 'ada', name: 'ADA/AODA Statement', icon: '\u267F', desc: 'Accessibility compliance statement', status: 'draft' },
  { id: 'gdpr', name: 'GDPR Notice', icon: '\uD83C\uDDEA\uD83C\uDDFA', desc: 'EU data protection notice', status: 'not_started' },
  { id: 'refund', name: 'Refund Policy', icon: '\uD83D\uDCB8', desc: 'Return and refund conditions', status: 'not_started' },
  { id: 'disclaimer', name: 'Disclaimer', icon: '\u26A0\uFE0F', desc: 'General liability disclaimer', status: 'draft' },
];

const CHECKLIST = [
  { item: 'Privacy Policy published on website', done: true, priority: 'Required' },
  { item: 'Terms of Service linked in footer', done: true, priority: 'Required' },
  { item: 'Cookie consent banner installed', done: false, priority: 'Required' },
  { item: 'SSL certificate active', done: true, priority: 'Required' },
  { item: 'GDPR data request form available', done: false, priority: 'If EU customers' },
  { item: 'Accessibility audit completed', done: false, priority: 'Recommended' },
  { item: 'Data retention policy documented', done: false, priority: 'Recommended' },
  { item: 'Employee data handling training', done: false, priority: 'Recommended' },
  { item: 'Refund policy displayed at checkout', done: false, priority: 'Required' },
  { item: 'Business license displayed', done: true, priority: 'Required' },
];

const GENERATED_DOCS = [
  { name: 'Privacy Policy v2', date: 'Feb 15, 2026', pages: 8, format: 'HTML' },
  { name: 'Terms of Service v1', date: 'Feb 15, 2026', pages: 12, format: 'HTML' },
  { name: 'Cookie Policy v1', date: 'Feb 16, 2026', pages: 3, format: 'HTML' },
];

function LegalCompliancePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('documents');
  const [selectedDoc, setSelectedDoc] = useState(null);

  const getStatusColor = (s) => ({ generated: '#10b981', draft: '#f59e0b', not_started: 'var(--text-muted)' }[s]);
  const getStatusLabel = (s) => ({ generated: 'Generated', draft: 'Draft', not_started: 'Not Started' }[s]);

  const completedCount = CHECKLIST.filter(c => c.done).length;
  const complianceScore = Math.round(completedCount / CHECKLIST.length * 100);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\uD83D\uDEE1\uFE0F'} Legal & Compliance</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>Auto-generate legal documents and track compliance.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Compliance Score</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: complianceScore >= 70 ? '#10b981' : complianceScore >= 40 ? '#f59e0b' : '#ef4444' }}>{complianceScore}%</p>
          <div style={{ height: 6, background: 'var(--bg)', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
            <div style={{ width: `${complianceScore}%`, height: '100%', background: complianceScore >= 70 ? '#10b981' : '#f59e0b', borderRadius: 3 }} />
          </div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Documents Generated</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: '#6366f1' }}>{DOC_TYPES.filter(d => d.status === 'generated').length}/{DOC_TYPES.length}</p>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Checklist Completed</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{completedCount}/{CHECKLIST.length}</p>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Jurisdiction</p>
          <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Alberta, CA</p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>PIPEDA applicable</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['documents', 'checklist', 'cookie', 'history'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 20px', background: tab === t ? '#6366f1' : 'var(--card)', color: tab === t ? '#fff' : 'var(--text-muted)', border: tab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t === 'cookie' ? 'Cookie Banner' : t}</button>
        ))}
      </div>

      {tab === 'documents' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {DOC_TYPES.map(d => (
            <div key={d.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 28 }}>{d.icon}</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{d.name}</h3>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.desc}</p>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: getStatusColor(d.status) }}>{getStatusLabel(d.status)}</span>
                <button style={{ padding: '6px 14px', background: d.status === 'generated' ? 'transparent' : '#6366f1', color: d.status === 'generated' ? '#6366f1' : '#fff', border: d.status === 'generated' ? '1px solid #6366f1' : 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                  {d.status === 'generated' ? 'View / Edit' : d.status === 'draft' ? 'Continue' : 'Generate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'checklist' && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          {CHECKLIST.map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, border: c.done ? 'none' : '2px solid var(--border)', background: c.done ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                {c.done && '\u2713'}
              </div>
              <span style={{ flex: 1, fontSize: 14, color: c.done ? 'var(--text-muted)' : 'var(--text)', textDecoration: c.done ? 'line-through' : 'none' }}>{c.item}</span>
              <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, background: c.priority === 'Required' ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)', color: c.priority === 'Required' ? '#ef4444' : '#3b82f6' }}>{c.priority}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'cookie' && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Cookie Consent Banner</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>Generate a GDPR/PIPEDA compliant cookie consent banner for your website.</p>
          <div style={{ padding: 16, background: '#1e293b', borderRadius: 12, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <p style={{ color: '#e2e8f0', fontSize: 13, flex: 1 }}>We use cookies to enhance your experience. By continuing to visit this site, you agree to our use of cookies.</p>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button style={{ padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500 }}>Accept All</button>
                <button style={{ padding: '8px 16px', background: 'transparent', color: '#e2e8f0', border: '1px solid #475569', borderRadius: 6, fontSize: 13 }}>Customize</button>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Embed Code</label>
            <code style={{ display: 'block', padding: 12, background: 'var(--bg)', borderRadius: 8, fontSize: 12, color: '#6366f1', border: '1px solid var(--border)' }}>
              {'<script src="https://forge.pages.dev/cookie-consent.js" data-business="your-id"></script>'}
            </code>
          </div>
          <button style={{ padding: '10px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Copy Embed Code</button>
        </div>
      )}

      {tab === 'history' && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 120px 80px 80px 120px', padding: '12px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            <span>Document</span><span>Generated</span><span>Pages</span><span>Format</span><span>Actions</span>
          </div>
          {GENERATED_DOCS.map((d, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 120px 80px 80px 120px', padding: '14px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 14 }}>
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>{d.name}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{d.date}</span>
              <span style={{ color: 'var(--text-muted)' }}>{d.pages}</span>
              <span style={{ color: 'var(--text-muted)' }}>{d.format}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={{ padding: '4px 10px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>View</button>
                <button style={{ padding: '4px 10px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Download</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default LegalCompliancePage;
